import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, FileText, BookOpen, ArrowRight, Loader2 } from "lucide-react";
import { getCategoryColor } from "@/lib/categoryColors";
import { useServerSearch, type ServerSearchResult } from "@/hooks/useServerSearch";

const SearchOverlay = ({ onClose }: { onClose: () => void }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { results, total, isLoading } = useServerSearch(query, 200);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSelect = useCallback(
    (url: string) => {
      onClose();
      navigate(url);
    },
    [onClose, navigate]
  );

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="max-w-2xl mx-auto mt-20 mx-4 sm:mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-primary shrink-0 animate-spin" />
            ) : (
              <Search className="w-5 h-5 text-muted-foreground shrink-0" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, guides, topics..."
              className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-base"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {query.trim() && !isLoading && results.length === 0 && (
              <div className="px-5 py-10 text-center text-muted-foreground">
                No results for "{query}"
              </div>
            )}

            {results.length > 0 && (
              <div className="py-2">
                {results.slice(0, 8).map((result) => (
                  <button
                    key={`${result.type}-${result.url}`}
                    onClick={() => handleSelect(result.url)}
                    className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-secondary/50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      {result.type === "article" ? (
                        <FileText className="w-4 h-4 text-primary" />
                      ) : (
                        <BookOpen className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[10px] font-semibold tracking-wider uppercase ${getCategoryColor(result.category || "").text}`}>
                          {result.category}
                        </span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {result.type === "article" ? "Article" : "Guide"}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-foreground line-clamp-1">
                        {result.title}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {result.excerpt}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-2" />
                  </button>
                ))}

                {total > 8 && (
                  <div className="px-5 py-3 text-center">
                    <Link
                      to={`/search?q=${encodeURIComponent(query)}`}
                      onClick={onClose}
                      className="text-sm text-primary font-semibold hover:underline underline-offset-4"
                    >
                      View all {total} results
                    </Link>
                  </div>
                )}
              </div>
            )}

            {!query.trim() && (
              <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                Search across articles, guides, and topics
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
